import { useContext, useEffect, useRef, useState } from "react";
import { useNavigateWithSameKey } from "../../../../../../../../../../../custom-hooks/navigateWithKey";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../../../../../../../../ServicePage";
import PageClasses from "../../../../../shared/PageClasses";
import { Box, Button } from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  FileUploadOutlined as FileUploadOutlinedIcon,
} from "@mui/icons-material";
import { handleProgressBar } from "../../../../../../../../../../custom-components/ProgressBar/ProgressBarWithAPI";
import { playwrightCSharpReqnrollStepDefinition } from "../../../../../../../../../../../services/webServices";
import {
  setPageClassState,
  setStepDefinitionState,
  setUploadedTestDataState,
} from "../../../../../../../../../../../store/slices/test-automation/web/flow-stages/playwirghtCSharpReqnrollSlice";
import {
  flattenArray,
  flattenObject,
} from "../../../../../../../../../../../utils/dataFlatteners";
import { downloadSingleFile } from "../../../../../../../../../../../utils/downloadUtils";

const ReqNRollPageClass = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  const { locationKey } = location.state;

  const { featureFiles, pageClass, uploadedTestData, automationPath } = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "playwrightCSharpReqNRoll"
      ] || {}
  );

  const { setLoadingState } = useContext(LoadingModalContext);
  const { setMessage, setMessagePopup } = useContext(MessagePopupContext);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }
  }, [locationKey]);

  const handleBackClick = () => {
    // Navigate to the previous step
    navigate(-1);
  };

  const handlePageClassUpdate = (updatedData) => {
    dispatch(setPageClassState(locationKey, updatedData));
  };

  const handleGenerateStepDefinition = async () => {
    if (!featureFiles || !pageClass) {
      setMessage("Missing required data for step definition generation");
      setMessagePopup(true);
      return;
    }

    let progressHandler = null;

    try {
      // Initialize progress bar
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 18000,
      });
      progressHandler.start("Generating step definitions...");

      // Call API to generate step definitions
      const response = await playwrightCSharpReqnrollStepDefinition({
        featureFileGenerated: featureFiles,
        pageClassData: pageClass,
        testData: uploadedTestData
      });

      // Store step definition in Redux
      dispatch(setStepDefinitionState(locationKey, response.data));

      // Navigate to step definition page
      navigate(`${automationPath}/step-definition`);
    } catch (error) {
      setMessage(
        error?.response?.data?.error ||
          "An error occurred while generating the step definitions."
      );
      setMessagePopup(true);
    } finally {
      setLoadingState({
        open: false,
        message: "",
        progress: null,
      });

      if (progressHandler) {
        progressHandler.stop();
      }
    }
  };

  const handleDownload = () => {
    let downloadContent;

    if (Array.isArray(pageClass)) downloadContent = flattenArray(pageClass);
    else if (!!pageClass && typeof pageClass === "object")
      downloadContent = flattenObject(pageClass);
    else downloadContent = pageClass;

    downloadSingleFile({
      fileType: "txt",
      fileName: "pageclasses",
      fileContent: downloadContent,
    });
  };

  // Trigger file input when button is clicked
  const handleUpload = () => {
    fileInputRef.current.click();
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/json" && !file.name.endsWith(".json")) {
      setMessage("Please upload a valid JSON file.");
      setMessagePopup(true);
      dispatch(setUploadedTestDataState(locationKey, null));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsedData = JSON.parse(e.target.result);
        dispatch(setUploadedTestDataState(locationKey, { name: file.name, data: parsedData }));
      } catch (err) {
        setMessage("Invalid JSON format.");
        setMessagePopup(true);
        dispatch(setUploadedTestDataState(locationKey, null));
      }
    };

    reader.readAsText(file);
  };

  return (
    <Box sx={{ height: "100%" }}>
      <PageClasses
        pageClass={pageClass}
        handlePageClass={handlePageClassUpdate}
        language={"csharp"}
      />

      <Box sx={{marginX: "24px"}}>
        Uploaded Test Data: "{uploadedTestData?.name || "Not uploaded"}"
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          p: 4,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        {/* Back Button */}
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBackClick}
          sx={{
            height: "40px",
            borderColor: "#0094FB",
            color: "#0094FB",
            textTransform: "none",
            fontWeight: 500,
            fontFamily: "Manrope",
            borderRadius: "8px",
            borderWidth: "2px",
            px: 3,
            py: 1,
            minWidth: { xs: "100%", sm: "auto" },
            "&:hover": {
              borderColor: "#0078D4",
              backgroundColor: "rgba(0, 148, 251, 0.04)",
              borderWidth: "2px",
            },
          }}
        >
          Back
        </Button>

        <Box className="flex-container-row" sx={{ gap: "8px" }}>
          {/* Hidden file input */}
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          {/* Upload test data Button */}
          <Button
            variant="outlined"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={handleUpload}
            sx={{
              height: "40px",
              borderColor: "#0094FB",
              color: "#0094FB",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              borderWidth: "2px",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              gap: "8px",
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
            }}
          >
            Upload Test Data
          </Button>

          {/* Download Button */}
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleDownload}
            disabled={!pageClass || pageClass.length === 0}
            sx={{
              height: "40px",
              borderColor: "#0094FB",
              color: "#0094FB",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              borderWidth: "2px",
              paddingTop: "8px",
              paddingRight: "16px",
              paddingBottom: "8px",
              paddingLeft: "16px",
              gap: "8px",
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
            }}
          >
            Download
          </Button>
          {/* Continue Button */}
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleGenerateStepDefinition}
            disabled={!pageClass || pageClass.length === 0}
            sx={{
              height: "40px",
              backgroundColor: "#0094FB",
              color: "white",
              textTransform: "none",
              fontWeight: 500,
              fontFamily: "Manrope",
              borderRadius: "8px",
              px: 3,
              py: 1,
              minWidth: { xs: "100%", sm: "auto" },
              "&:hover": {
                backgroundColor: "#0078D4",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
                color: "#666666",
              },
            }}
          >
            Generate Step Definition
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ReqNRollPageClass;
